import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  ArrowDown, ArrowUp, CheckCircle2, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, FolderPlus, Hash, Inbox,
  Loader2, Lock, MessageSquareQuote, MessageSquareReply, MoreHorizontal, Pencil, ShieldAlert, ShieldCheck, Star, Trash2,
} from 'lucide-react';
import {
  waysApi, type WaysSection, type WaysSidebarChannel, type WaysSidebarConversation, type WaysSidebarItem,
  type WaysSidebarProject,
} from '../../services/waysApi';
import type { ApprovalRequest } from '../../services/api';

/**
 * The person's Ways sidebar (Zane, 2026-10-18), in the sections and order Ways serves: Active
 * (approvals waiting, then replies owed, then unread), Favorites, the person's own sections, then
 * General by most recent use.
 *
 * Every level folds -- a section, a project within it, and a project's general channel, which
 * holds its security and epic channels -- and what is folded is kept by Ways per person, so it
 * follows them across devices. Anything can be starred into Favorites, put in a section from its
 * ... menu, or dragged onto a section; an item lives in one section at a time (Ways moves it).
 */

type ItemType = 'channel' | 'conversation';
const DRAG_ITEM = 'application/x-ways-item';
const DRAG_SECTION = 'application/x-ways-section';
// `requests` (ways-api 0.22.0, `<key>/requests`): asks addressed to the project, next to security.
const KIND_ORDER: Record<string, number> = { project: 0, security: 1, requests: 2, epic: 3 };

/** A one-line title box, used for a new section and for renaming one. */
function InlineTitle({ initial = '', onDone, placeholder }: {
  initial?: string;
  placeholder: string;
  onDone: (title: string | null) => void;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const finish = (ok: boolean) => onDone(ok && v.trim() && v.trim() !== initial ? v.trim() : null);
  return (
    <input ref={ref} value={v} placeholder={placeholder} maxLength={60}
      onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') finish(true); if (e.key === 'Escape') finish(false); }}
      onBlur={() => finish(true)}
      onClick={(e) => e.stopPropagation()}
      className="w-full px-2 py-0.5 text-xs border border-accent rounded bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none" />
  );
}

function Badge({ n, tone = 'accent', title, icon }: {
  n?: number; tone?: 'accent' | 'amber' | 'sky'; title?: string; icon?: ReactNode;
}) {
  if (!n) return null;
  return (
    <span title={title} className={clsx('shrink-0 inline-flex items-center gap-0.5 text-[10px] tabular-nums px-1.5 rounded-full',
      tone === 'accent' && 'bg-accent text-white',
      tone === 'amber' && 'bg-amber-500 text-white',
      tone === 'sky' && 'bg-sky-600 text-white')}>{icon}{n}</span>
  );
}

function Fold({ open, onToggle, label }: { open: boolean; onToggle: () => void; label: string }) {
  return (
    <button type="button" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="p-0.5 shrink-0 rounded text-gray-400 hover:text-accent">
      {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
    </button>
  );
}

/** A small menu anchored to a "..." button. */
function Menu({ open, onOpen, label, children }: {
  open: boolean; onOpen: () => void; label: string; children: ReactNode;
}) {
  return (
    <span className="relative shrink-0">
      <button type="button" title={label} aria-label={label}
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        className="p-0.5 rounded text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-accent">
        <MoreHorizontal className="w-3.5 h-3.5" />
      </button>
      {open ? (
        <div onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-5 z-30 w-52 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-sm normal-case tracking-normal font-normal">
          {children}
        </div>
      ) : null}
    </span>
  );
}

function MenuItem({ icon, children, onClick, danger, disabled }: {
  icon: ReactNode; children: ReactNode; onClick: () => void; danger?: boolean; disabled?: boolean;
}) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      className={clsx('w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40',
        danger ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-200')}>
      {icon}<span className="truncate">{children}</span>
    </button>
  );
}

interface Props {
  sections: WaysSection[] | null;
  /** Folded nodes as Ways last returned them. */
  collapsedNodes: string[];
  /** Opened project rows as Ways last returned them (projects view). */
  expandedNodes: string[];
  /** Each opened project's channels: the list, still loading, or not readable. */
  projectChannels: Record<string, WaysSidebarChannel[] | 'loading' | 'denied'>;
  onOpenProject: (projectId: string) => void;
  /** Per project: how many closed epics are hidden, and whether the person shows them. */
  closedEpics: Record<string, { hidden: number; show: boolean }>;
  onToggleClosedEpics: (projectId: string, show: boolean) => void;
  /** PMO approval requests this person may decide, by project. */
  approvalsByProject: Record<string, ApprovalRequest[]>;
  activeApproval: string | null;
  onOpenApproval: (requestId: string) => void;
  /** Opens a project's governance (off switch, governor mode, findings); projects stopped now. */
  onOpenGovernance: (projectId: string) => void;
  stoppedProjects: Set<string>;
  active: string | null;
  projectName: (id?: string | null) => string;
  onOpenChannel: (key: string) => void;
  onOpenConversation: (channelKey: string, seq: number) => void;
  onChanged: () => void;
  onError: (e: unknown) => void;
  /** Load what a windowed section left out (Active's next page, General's every project). */
  onLoadMore?: (sectionId: string) => void;
  /** The section whose "Load more" is in flight. */
  loadingMore?: string | null;
}

export default function WaysSidebar({ sections, collapsedNodes, expandedNodes, projectChannels, onOpenProject,
  closedEpics, onToggleClosedEpics,
  approvalsByProject, activeApproval, onOpenApproval, onOpenGovernance, stoppedProjects, active, projectName, onOpenChannel,
  onOpenConversation, onChanged, onError, onLoadMore, loadingMore }: Props) {
  const [openApprovals, setOpenApprovals] = useState<Set<string>>(() => new Set());
  // Folds: Ways' answer, overridden at once by the person's own clicks (sent in the background).
  const [localFold, setLocalFold] = useState<Record<string, boolean>>({});
  const folded = useMemo(() => {
    const s = new Set(collapsedNodes);
    for (const sec of sections || []) if (sec.collapsed) s.add(sec.id);
    for (const [node, c] of Object.entries(localFold)) { if (c) s.add(node); else s.delete(node); }
    return s;
  }, [collapsedNodes, sections, localFold]);

  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [creating, setCreating] = useState<null | { then?: { type: ItemType; id: string } }>(null);
  const [dropOn, setDropOn] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The person's own ordering: Favorites and their sections (positions count among these).
  const own = useMemo(() => (sections || []).filter((s) => !s.builtin || s.id === 'favorites'), [sections]);
  const ownIds = useMemo(() => own.map((s) => s.id), [own]);
  const favorites = useMemo(() => {
    const f = (sections || []).find((s) => s.id === 'favorites');
    return new Set((f?.items || []).map((i) => `${i.type}:${i.id}`));
  }, [sections]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setMenuFor(null);
    try { await fn(); onChanged(); } catch (e) { onError(e); } finally { setBusy(false); }
  };

  // One save at a time per node, and only the latest wanted state: parallel saves to different
  // Ways replicas could land out of order and fold the node back on the next refresh. The
  // person's own choice stays in front of whatever a refresh says for the rest of the visit.
  const foldWanted = useRef<Record<string, boolean>>({});
  const foldSending = useRef<Set<string>>(new Set());
  const sendFold = async (node: string) => {
    if (foldSending.current.has(node)) return;
    foldSending.current.add(node);
    try {
      let sent: boolean | undefined;
      while (foldWanted.current[node] !== sent) {
        sent = foldWanted.current[node];
        await waysApi.setCollapsed(node, sent);
      }
    } catch (e) {
      onError(e);
    } finally {
      foldSending.current.delete(node);
    }
  };
  const setFold = (nodes: string[], collapsed: boolean) => {
    setLocalFold((f) => ({ ...f, ...Object.fromEntries(nodes.map((n) => [n, collapsed])) }));
    for (const n of nodes) { foldWanted.current[n] = collapsed; sendFold(n); }
  };
  const toggle = (node: string) => setFold([node], !folded.has(node));

  const star = (type: ItemType, id: string) => {
    const on = favorites.has(`${type}:${id}`);
    run(() => (on ? waysApi.removeFromSection('favorites', type, id) : waysApi.addToSection('favorites', type, id)));
  };

  // ── drag and drop ────────────────────────────────────────────────────────
  const dragItem = (e: DragEvent, type: ItemType, id: string) => {
    e.dataTransfer.setData(DRAG_ITEM, JSON.stringify({ type, id }));
    e.dataTransfer.effectAllowed = 'move';
  };
  const dragSection = (e: DragEvent, id: string) => {
    e.dataTransfer.setData(DRAG_SECTION, id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const acceptsDrop = (e: DragEvent, target: WaysSection) => {
    const t = e.dataTransfer.types;
    const ok = (t.includes(DRAG_ITEM) && (target.id === 'favorites' || !target.builtin))
      || (t.includes(DRAG_SECTION) && ownIds.includes(target.id));
    if (ok) { e.preventDefault(); setDropOn(target.id); }
  };
  const drop = (e: DragEvent, target: WaysSection, position?: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDropOn(null);
    const item = e.dataTransfer.getData(DRAG_ITEM);
    const sec = e.dataTransfer.getData(DRAG_SECTION);
    if (item) {
      const { type, id } = JSON.parse(item) as { type: ItemType; id: string };
      if (target.id === 'favorites' || !target.builtin) run(() => waysApi.addToSection(target.id, type, id, position));
    } else if (sec && sec !== target.id && ownIds.includes(target.id)) {
      run(() => waysApi.updateSection(sec, { position: ownIds.indexOf(target.id) }));
    }
  };

  // ── menus ────────────────────────────────────────────────────────────────
  const itemMenu = (type: ItemType, id: string, inSection: WaysSection) => {
    const k = `${inSection.id}|${type}:${id}`;
    const removable = inSection.id === 'favorites' || !inSection.builtin;
    return (
      <Menu open={menuFor === k} onOpen={() => setMenuFor(menuFor === k ? null : k)} label="Add to a section">
        <div className="px-3 pt-1 pb-0.5 text-[11px] uppercase tracking-wide text-gray-400">Add to section</div>
        {own.filter((s) => s.id !== inSection.id).map((s) => (
          <MenuItem key={s.id} disabled={busy}
            icon={s.id === 'favorites' ? <Star className="w-3.5 h-3.5 text-amber-500" /> : <FolderPlus className="w-3.5 h-3.5 text-gray-400" />}
            onClick={() => run(() => waysApi.addToSection(s.id, type, id))}>{s.title}</MenuItem>
        ))}
        <MenuItem icon={<FolderPlus className="w-3.5 h-3.5 text-gray-400" />} disabled={busy}
          onClick={() => { setMenuFor(null); setCreating({ then: { type, id } }); }}>New section…</MenuItem>
        {removable ? (
          <MenuItem danger disabled={busy} icon={<Trash2 className="w-3.5 h-3.5" />}
            onClick={() => run(() => waysApi.removeFromSection(inSection.id, type, id))}>
            Remove from {inSection.title}
          </MenuItem>
        ) : null}
      </Menu>
    );
  };

  const starButton = (type: ItemType, id: string) => {
    const on = favorites.has(`${type}:${id}`);
    return (
      <button type="button" title={on ? 'Remove from Favorites' : 'Add to Favorites'}
        aria-label={on ? 'Remove from Favorites' : 'Add to Favorites'} disabled={busy}
        onClick={(e) => { e.stopPropagation(); star(type, id); }}
        className={clsx('p-0.5 shrink-0 rounded hover:text-amber-500',
          on ? 'text-amber-500' : 'text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100')}>
        <Star className={clsx('w-3.5 h-3.5', on && 'fill-current')} />
      </button>
    );
  };

  const governanceButton = (pid: string) => (
    <button type="button" title={stoppedProjects.has(pid) ? 'Agents are stopped here: governance' : 'Governance: off switch, governor, findings'}
      aria-label="Governance" onClick={(e) => { e.stopPropagation(); onOpenGovernance(pid); }}
      className={clsx('p-0.5 shrink-0 rounded hover:text-accent',
        stoppedProjects.has(pid) ? 'text-red-500' : 'text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100')}>
      <ShieldCheck className="w-3.5 h-3.5" />
    </button>
  );

  // ── rows ─────────────────────────────────────────────────────────────────
  const channelRow = (c: WaysSidebarChannel, s: WaysSection, depth: number, label: string,
    fold?: { open: boolean; node: string }) => (
    <div key={`c:${c.id}`} role="button" tabIndex={0} title={`${c.name || label} (${c.key})`} draggable
      onDragStart={(e) => dragItem(e, 'channel', c.id)}
      onDragOver={(e) => acceptsDrop(e, s)}
      onDrop={(e) => drop(e, s, s.items.findIndex((i) => i.type === 'channel' && i.id === c.id))}
      onClick={() => onOpenChannel(c.key)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpenChannel(c.key); }}
      style={{ paddingLeft: `${depth * 14 + 4}px` }}
      className={clsx('group w-full flex items-center gap-1 pr-1 py-1 rounded text-sm cursor-pointer',
        active === c.key ? 'bg-accent/10 text-accent font-medium'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60')}>
      {fold ? <Fold open={fold.open} onToggle={() => toggle(fold.node)} label={label} />
        : <span className="w-[18px] shrink-0" />}
      {c.kind === 'security' ? <ShieldAlert className="w-3.5 h-3.5 shrink-0 opacity-60" />
        : c.kind === 'requests' ? <Inbox className="w-3.5 h-3.5 shrink-0 opacity-60" />
        : <Hash className="w-3.5 h-3.5 shrink-0 opacity-60" />}
      <span className="flex-1 min-w-0 line-clamp-2 break-words leading-snug">{label}</span>
      {c.readOnly ? (
        <span title={c.kind === 'epic' ? 'Read-only: this epic is closed' : 'Read-only'} className="shrink-0 text-gray-400">
          <Lock className="w-3 h-3" />
        </span>
      ) : null}
      <Badge n={c.approvals} tone="amber" title="Approvals waiting for you" icon={<CheckCircle2 className="w-2.5 h-2.5" />} />
      <Badge n={c.replies} tone="sky" title="Replies you owe" icon={<MessageSquareReply className="w-2.5 h-2.5" />} />
      <Badge n={c.unread} title="Unread" />
      {starButton('channel', c.id)}
      {itemMenu('channel', c.id, s)}
    </div>
  );

  const conversationRow = (v: WaysSidebarConversation, s: WaysSection) => (
    <div key={`v:${v.id}`} role="button" tabIndex={0} title={v.id} draggable
      onDragStart={(e) => dragItem(e, 'conversation', v.id)}
      onDragOver={(e) => acceptsDrop(e, s)}
      onDrop={(e) => drop(e, s, s.items.findIndex((i) => i.type === 'conversation' && i.id === v.id))}
      onClick={() => onOpenConversation(v.channelKey, v.seq)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpenConversation(v.channelKey, v.seq); }}
      className="group w-full flex items-center gap-1 pl-1 pr-1 py-1 rounded text-sm cursor-pointer text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60">
      <span className="w-[18px] shrink-0" />
      <MessageSquareQuote className="w-3.5 h-3.5 shrink-0 opacity-60" />
      <span className="flex-1 min-w-0 line-clamp-2 break-words leading-snug" title={v.headline || ''}>
        {v.headline || `#${v.seq}`}
        <span className="ml-1 text-[11px] text-gray-400">{v.channelName || v.channelKey}</span>
      </span>
      {starButton('conversation', v.id)}
      {itemMenu('conversation', v.id, s)}
    </div>
  );

  /** "Approvals (n)" under a project in Active: PMO requests this person may decide there. */
  const approvalsFor = (pid: string) => {
    const list = approvalsByProject[pid] || [];
    if (!list.length) return null;
    const node = `approvals:${pid}`;
    const open = openApprovals.has(node);
    return (
      <div key={node}>
        <button type="button" aria-expanded={open}
          onClick={() => setOpenApprovals((o) => { const n = new Set(o); if (n.has(node)) n.delete(node); else n.add(node); return n; })}
          style={{ paddingLeft: '18px' }}
          className="w-full flex items-center gap-1 pr-1 py-1 rounded text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20">
          {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1 text-left">Approvals ({list.length})</span>
        </button>
        {open ? list.map((r) => (
          <button key={r.requestId} type="button" onClick={() => onOpenApproval(r.requestId)}
            title={`${r.itemTitle || r.itemId || ''} -> ${r.to}, from ${r.requestedBy}`}
            style={{ paddingLeft: '40px' }}
            className={clsx('w-full flex items-center gap-1 pr-1 py-1 rounded text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700/60',
              activeApproval === r.requestId ? 'bg-accent/10 text-accent' : 'text-gray-700 dark:text-gray-300')}>
            <span className="flex-1 min-w-0 line-clamp-2 break-words leading-snug">
              {r.itemKey ? <span className="font-mono text-[11px] text-gray-500 mr-1">{r.itemKey}</span> : null}
              {r.itemTitle || r.itemId} <span className="text-[11px] text-gray-400">&rarr; {r.to}</span>
            </span>
          </button>
        )) : null}
      </div>
    );
  };

  /** A project's channels nested: general, then (under it) security and the epics. */
  const projectChannelsTree = (chansIn: WaysSidebarChannel[], s: WaysSection, depth: number) => {
    const chans = chansIn.slice().sort((a, b) =>
      ((KIND_ORDER[a.kind] ?? 4) - (KIND_ORDER[b.kind] ?? 4)) || (a.name || '').localeCompare(b.name || ''));
    const general = chans.find((c) => c.kind === 'project');
    const rest = chans.filter((c) => c !== general);
    const label = (c: WaysSidebarChannel) =>
      c.kind === 'project' ? 'general' : c.kind === 'security' ? 'security' : c.kind === 'requests' ? 'requests' : c.name || c.key;
    const gOpen = general ? !folded.has(general.id) : true;
    return (
      <>
        {general ? channelRow(general, s, depth, 'general',
          rest.length ? { open: gOpen, node: general.id } : undefined) : null}
        {gOpen ? rest.map((c) => channelRow(c, s, general ? depth + 1 : depth, label(c))) : null}
      </>
    );
  };

  /** A project row of the projects view: folded unless opened; its channels load on opening. */
  const projectRow = (p: WaysSidebarProject, s: WaysSection) => {
    const open = p.id in localFold ? !localFold[p.id] : (!!p.expanded || expandedNodes.includes(p.id));
    const loaded = projectChannels[p.id];
    const toggleProject = () => {
      setFold([p.id], open);
      if (!open && loaded === undefined) onOpenProject(p.id);
    };
    return (
      <div key={`p:${p.id}`}>
        <div className="group flex items-center gap-1 pl-1 pr-1 py-1 rounded text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/60 cursor-pointer"
          onClick={toggleProject}>
          <Fold open={open} onToggle={toggleProject} label={p.name} />
          <span className="flex-1 min-w-0 line-clamp-2 break-words leading-snug font-medium" title={p.name}>{p.name}</span>
          {p.readOnly ? <span title="Read-only" className="shrink-0 text-gray-400"><Lock className="w-3 h-3" /></span> : null}
          <Badge n={(p.approvals || 0) + (p.pmoApprovals || 0)} tone="amber" title="Approvals waiting for you" icon={<CheckCircle2 className="w-2.5 h-2.5" />} />
          <Badge n={p.replies} tone="sky" title="Replies you owe" icon={<MessageSquareReply className="w-2.5 h-2.5" />} />
          {!open ? <Badge n={p.unread} title="Unread" /> : null}
          {governanceButton(p.id)}
        </div>
        {open ? (
          loaded === 'loading' || loaded === undefined ? (
            <p className="pl-8 py-1 text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading&hellip;</p>
          ) : loaded === 'denied' ? (
            <p className="pl-8 py-1 text-xs text-gray-400">You cannot read this project&rsquo;s channels.</p>
          ) : (
            <>
              {projectChannelsTree(loaded, s, 1)}
              {closedEpics[p.id] && (closedEpics[p.id].hidden > 0 || closedEpics[p.id].show) ? (
                <button type="button" onClick={() => onToggleClosedEpics(p.id, !closedEpics[p.id].show)}
                  className="flex items-center gap-1 pl-8 py-1 text-xs text-gray-500 hover:text-accent">
                  <Lock className="w-3 h-3" />
                  {closedEpics[p.id].show ? 'Hide closed epics'
                    : `Show ${closedEpics[p.id].hidden} closed epic${closedEpics[p.id].hidden === 1 ? '' : 's'}`}
                </button>
              ) : null}
            </>
          )
        ) : null}
      </div>
    );
  };

  /**
   * A section's items as a tree: project -> its general channel -> that channel's security and
   * epic channels. A project whose general channel is not in this section lists the others
   * directly under it. Project rows (General, in the projects view) load their channels when
   * opened. Conversations follow, at the section's level. In Active, a project with PMO approvals
   * waiting for this person shows them under it.
   */
  const sectionBody = (s: WaysSection) => {
    const order: string[] = [];
    const by = new Map<string, WaysSidebarChannel[]>();
    const loose: WaysSidebarItem[] = [];
    for (const i of s.items) {
      if (i.type !== 'channel' || !i.projectId) { loose.push(i); continue; }
      if (!by.has(i.projectId)) { by.set(i.projectId, []); order.push(i.projectId); }
      by.get(i.projectId)!.push(i);
    }
    if (s.id === 'attention') {
      for (const pid of Object.keys(approvalsByProject)) {
        if (approvalsByProject[pid]?.length && !by.has(pid)) { by.set(pid, []); order.push(pid); }
      }
    }
    return (
      <>
        {order.map((pid) => {
          const chans = by.get(pid)!;
          const pnode = `project:${pid.replace(/^project:/, '')}`;
          const pOpen = !folded.has(pnode);
          const unread = chans.reduce((n, c) => n + (c.unread || 0), 0);
          return (
            <div key={pid}>
              <div className="group flex items-center gap-1 pl-1 pr-1 py-1 rounded text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/60 cursor-pointer"
                onClick={() => toggle(pnode)}>
                <Fold open={pOpen} onToggle={() => toggle(pnode)} label={projectName(pid)} />
                <span className="flex-1 min-w-0 line-clamp-2 break-words leading-snug font-medium" title={projectName(pid)}>{projectName(pid)}</span>
                {!pOpen ? <Badge n={unread} /> : null}
                {governanceButton(pid)}
              </div>
              {pOpen ? (
                <>
                  {s.id === 'attention' ? approvalsFor(pid) : null}
                  {projectChannelsTree(chans, s, 1)}
                </>
              ) : null}
            </div>
          );
        })}
        {loose.map((i) => (i.type === 'channel'
          ? channelRow(i, s, 0, i.name || i.key)
          : i.type === 'project' ? projectRow(i, s)
            : conversationRow(i, s)))}
      </>
    );
  };

  // Every node under a section, for "Collapse all" / "Expand all".
  const nodesIn = (s: WaysSection) => {
    const out = new Set<string>();
    for (const i of s.items) {
      if (i.type === 'project') { out.add(i.id); continue; }
      if (i.type !== 'channel' || !i.projectId) continue;
      out.add(`project:${i.projectId.replace(/^project:/, '')}`);
      if (i.kind === 'project') out.add(i.id);
    }
    return [...out];
  };

  const sectionMenu = (s: WaysSection) => {
    if (s.id === 'attention' || s.id === 'general') {
      return (
        <Menu open={menuFor === `sec|${s.id}`} onOpen={() => setMenuFor(menuFor === `sec|${s.id}` ? null : `sec|${s.id}`)} label="Section actions">
          <MenuItem icon={<ChevronsDownUp className="w-3.5 h-3.5" />} onClick={() => { setMenuFor(null); setFold(nodesIn(s), true); }}>Collapse all</MenuItem>
          <MenuItem icon={<ChevronsUpDown className="w-3.5 h-3.5" />} onClick={() => { setMenuFor(null); setFold(nodesIn(s), false); }}>Expand all</MenuItem>
        </Menu>
      );
    }
    const pos = ownIds.indexOf(s.id);
    return (
      <Menu open={menuFor === `sec|${s.id}`} onOpen={() => setMenuFor(menuFor === `sec|${s.id}` ? null : `sec|${s.id}`)} label="Section actions">
        {!s.builtin ? (
          <MenuItem icon={<Pencil className="w-3.5 h-3.5" />} onClick={() => { setMenuFor(null); setRenaming(s.id); }}>Rename</MenuItem>
        ) : null}
        <MenuItem icon={<ArrowUp className="w-3.5 h-3.5" />} disabled={busy || pos <= 0}
          onClick={() => run(() => waysApi.updateSection(s.id, { position: pos - 1 }))}>Move up</MenuItem>
        <MenuItem icon={<ArrowDown className="w-3.5 h-3.5" />} disabled={busy || pos >= ownIds.length - 1}
          onClick={() => run(() => waysApi.updateSection(s.id, { position: pos + 1 }))}>Move down</MenuItem>
        <MenuItem icon={<ChevronsDownUp className="w-3.5 h-3.5" />} onClick={() => { setMenuFor(null); setFold(nodesIn(s), true); }}>Collapse all</MenuItem>
        <MenuItem icon={<ChevronsUpDown className="w-3.5 h-3.5" />} onClick={() => { setMenuFor(null); setFold(nodesIn(s), false); }}>Expand all</MenuItem>
        {!s.builtin ? (
          <MenuItem danger disabled={busy} icon={<Trash2 className="w-3.5 h-3.5" />}
            onClick={() => {
              if (window.confirm(`Delete "${s.title}"? Its channels and conversations go back to General.`)) {
                run(() => waysApi.deleteSection(s.id));
              }
            }}>Delete section</MenuItem>
        ) : null}
      </Menu>
    );
  };

  if (sections === null) {
    return (
      <p className="flex items-center gap-2 px-3 py-4 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </p>
    );
  }

  const hasOwnSections = sections.some((s) => !s.builtin);

  return (
    <nav className="p-2 space-y-2" onClick={() => setMenuFor(null)}>
      {sections.map((s) => {
        const open = !folded.has(s.id);
        const movable = ownIds.includes(s.id);
        return (
          <section key={s.id}
            onDragOver={(e) => acceptsDrop(e, s)} onDragLeave={() => setDropOn(null)} onDrop={(e) => drop(e, s)}
            className={clsx('rounded', dropOn === s.id && 'ring-2 ring-accent/60 bg-accent/5')}>
            <div className="group flex items-center gap-1 px-0.5" draggable={movable}
              onDragStart={movable ? (e) => dragSection(e, s.id) : undefined}>
              <Fold open={open} onToggle={() => toggle(s.id)} label={s.title} />
              {renaming === s.id ? (
                <InlineTitle initial={s.title} placeholder="Section name"
                  onDone={(t) => { setRenaming(null); if (t) run(() => waysApi.updateSection(s.id, { title: t })); }} />
              ) : (
                <button type="button" onClick={() => toggle(s.id)}
                  className={clsx('flex-1 min-w-0 flex items-center gap-1 py-1 text-xs font-semibold uppercase tracking-wide text-left',
                    s.id === 'attention' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400')}>
                  {s.id === 'favorites' ? <Star className="w-3 h-3 shrink-0 text-amber-500 fill-current" /> : null}
                  <span className="truncate">{s.title}</span>
                  <span className="font-normal tabular-nums">{s.items.length || ''}</span>
                </button>
              )}
              {sectionMenu(s)}
            </div>
            {open ? (
              s.items.length ? (
                <div className="space-y-0.5">{sectionBody(s)}</div>
              ) : (
                <p className="pl-6 py-1 text-xs text-gray-400">
                  {s.id === 'attention' ? 'Nothing waiting for you.'
                    : s.id === 'favorites' ? 'Star a channel or message to keep it here.'
                      : 'Drag a channel here, or use … → Add to section.'}
                </p>
              )
            ) : null}
            {open && onLoadMore && s.more && (s.more.nextOffset != null || s.more.query) ? (
              <button type="button" disabled={loadingMore === s.id}
                onClick={(e) => { e.stopPropagation(); onLoadMore(s.id); }}
                className="ml-6 my-0.5 text-xs text-accent hover:underline disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded">
                {loadingMore === s.id ? 'Loading…'
                  : s.more.nextOffset != null
                    ? `Load more${s.more.count ? ` (${Math.max(0, s.more.count - s.items.length)} more)` : ''}`
                    : `Show all projects${s.more.count ? ` (${s.more.count})` : ''}`}
              </button>
            ) : null}
            {s.id === 'favorites' && !hasOwnSections && open && s.items.length ? (
              <p className="pl-6 py-1 text-xs text-gray-400">
                Star a channel or message to keep it here ·{' '}
                <button type="button" className="text-accent hover:underline" onClick={(e) => { e.stopPropagation(); setCreating({}); }}>
                  + New section
                </button>
              </p>
            ) : null}
          </section>
        );
      })}
      {creating ? (
        <div className="px-2">
          <InlineTitle placeholder="New section name"
            onDone={(t) => {
              const then = creating.then;
              setCreating(null);
              if (t) {
                run(async () => {
                  const r = await waysApi.createSection(t);
                  if (then) await waysApi.addToSection(r.section.id, then.type, then.id);
                });
              }
            }} />
        </div>
      ) : (
        <button type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); setCreating({}); }}
          className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-accent">
          <FolderPlus className="w-3.5 h-3.5" /> New section
        </button>
      )}
    </nav>
  );
}
